export function formatIndianNumber(number) {
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
}

const DEFAULT_DELIVERY_FEE = 150;
const FREE_DELIVERY_THRESHOLD = 2500;
const COD_MIN = 500;
const COD_MAX = 20000;

/**
 * Net products amount after discounts (Total MRP - Total Discount).
 */
export function getNetPayable(cart) {
    const total = Number(cart?.total) || 0;
    const discountedTotal = Number(cart?.discountedTotal) || 0;
    return total - discountedTotal;
}

/**
 * Per-unit overweight charge for a cart item, stored as `owc`
 * (mirrors the mobile app's cart payload).
 */
export function getItemOwc(item) {
    return Number(item?.owc) || 0;
}

/**
 * Sum of per-item overweight charges (owc * quantity) across the cart.
 */
export function getTotalItemOwc(cart) {
    const products = Array.isArray(cart?.products) ? cart.products : [];
    return products.reduce((sum, item) => {
        const quantity = Number(item?.quantity) || 0;
        return sum + getItemOwc(item) * quantity;
    }, 0);
}

/**
 * Delivery fee following the mobile-app priority rule:
 *   overweightTotal = total_item_owc != 0
 *       ? total_item_owc
 *       : (net_payable < 2500) ? 150 : 0
 */
export function getOverweightTotal(cart) {
    const products = Array.isArray(cart?.products) ? cart.products : [];
    if (products.length === 0) {
        return 0;
    }
    const totalItemOwc = getTotalItemOwc(cart);
    if (totalItemOwc !== 0) {
        return totalItemOwc;
    }
    return getNetPayable(cart) < FREE_DELIVERY_THRESHOLD ? DEFAULT_DELIVERY_FEE : 0;
}

/**
 * COD eligibility, mirroring the mobile-app checkout checks:
 *   1. Every product must have cod_status == 1.
 *   2. Net products amount must be between 500 and 20,000.
 * Returns the first product that blocks COD so the UI can name it.
 */
export function getCodEligibility(cart) {
    const products = Array.isArray(cart?.products) ? cart.products : [];
    const net = getNetPayable(cart);

    const blockingProduct = products.find((item) => Number(item?.cod_status) !== 1);

    if (blockingProduct) {
        return {
            available: false,
            blockingProduct,
            message: `COD not available for "${blockingProduct.name}" in your cart`,
        };
    }
    if (net < COD_MIN) {
        return { available: false, blockingProduct: null, message: `COD not available for orders below ₹${COD_MIN}` };
    }
    if (net > COD_MAX) {
        return { available: false, blockingProduct: null, message: `COD not available for orders above ₹${COD_MAX.toLocaleString('en-IN')}` };
    }
    return { available: true, blockingProduct: null, message: 'COD available' };
}

/**
 * 5% COD surcharge on the net products amount: (ProductsAmount * 5) / 100.
 * No flooring — matches the mobile app exactly.
 */
export function getCodAmount(cart) {
    return (getNetPayable(cart) * 5) / 100;
}